// src/contexts/AttendanceContext.js
// PresentPlus — AttendanceContext v6 (clean, no warnings)

import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
  setDoc,
} from "firebase/firestore";

import { db } from "../firebase";
import { useAuth } from "./AuthContext";

const AttendanceContext = createContext(null);

export function useAttendance() {
  return useContext(AttendanceContext);
}

export function AttendanceProvider({ children }) {
  const { profile } = useAuth();
  const schoolId = profile?.schoolId || null;

  const [students, setStudents] = useState([]);
  const [todayAttendance, setTodayAttendance] = useState([]);
  const [loading, setLoading] = useState(true);

  const today = useMemo(() => new Date().toISOString().split("T")[0], []);

  // Load students
  useEffect(() => {
    if (!schoolId) {
      setStudents([]);
      setLoading(false);
      return;
    }

    const unsub = onSnapshot(
      collection(db, "schools", schoolId, "students"),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setStudents(list);
      },
      () => setStudents([])
    );

    return () => unsub();
  }, [schoolId]);

  // Load today's attendance
  useEffect(() => {
    if (!schoolId) {
      setTodayAttendance([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, "schools", schoolId, "attendance"),
      where("date", "==", today)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setTodayAttendance(list);
        setLoading(false);
      },
      () => {
        setTodayAttendance([]);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [schoolId, today]);

  // Save attendance
  const saveAttendance = async (classId, studentId, status) => {
    if (!schoolId) throw new Error("No school linked");

    const id = `${today}_${studentId}`;

    const payload = {
      studentId,
      classId,
      status,
      date: today,
      time: new Date().toLocaleTimeString(),
      schoolId,
    };

    await setDoc(doc(db, "schools", schoolId, "attendance", id), payload);

    await setDoc(
      doc(db, "schools", schoolId, "students", studentId, "attendance_daily", id),
      payload
    );
  };

  // Stats
  const presentToday = todayAttendance.filter(
    (a) => String(a.status || "").toLowerCase() === "present"
  ).length;

  const absentToday = todayAttendance.filter(
    (a) => String(a.status || "").toLowerCase() === "absent"
  ).length;

  const lateToday = todayAttendance.filter(
    (a) => String(a.status || "").toLowerCase() === "late"
  ).length;

  const attendanceRate =
    students.length > 0
      ? Math.round((presentToday / students.length) * 100)
      : 0;

  return (
    <AttendanceContext.Provider
      value={{
        students,
        todayAttendance,
        presentToday,
        absentToday,
        lateToday,
        attendanceRate,
        saveAttendance,
        loading,
        schoolId,
      }}
    >
      {children}
    </AttendanceContext.Provider>
  );
}

export default AttendanceProvider;